import { OnboardingSession } from "models/stateTypes/authState";
import { OnboardingPersonSession } from "models/stateTypes/onboardingState";
import {
  GENERIC_ONBOARDING_SCOPE,
  ONBOARDING_ACTIVE_COMPANY_KEY,
  ONBOARDING_PERSON_SESSION_KEY,
  getOnboardingSessionStorageKey,
} from "./onboardingPerson";

const read = <T>(storage: Storage, key: string): T | null => {
  const item = storage.getItem(key);
  return item !== null ? (JSON.parse(item) as T) : null;
};

/**
 * Where a company session lives. An invite is a link that is opened in whatever tab, so its slot is
 * shared as before. The generic flow has one slot for every company, so shared storage would let a
 * tab on one company overwrite the session of another tab on a different one; per-tab storage keeps
 * each tab on its own company, survives that tab's reload, and goes with the tab.
 */
const companySessionStorage = (scope: string): Storage =>
  scope === GENERIC_ONBOARDING_SCOPE ? sessionStorage : localStorage;

export const readCompanySession = (scope: string) =>
  read<OnboardingSession>(companySessionStorage(scope), getOnboardingSessionStorageKey(scope));

export const writeCompanySession = (scope: string, session: OnboardingSession) =>
  companySessionStorage(scope).setItem(getOnboardingSessionStorageKey(scope), JSON.stringify(session));

export const removeCompanySession = (scope: string) =>
  companySessionStorage(scope).removeItem(getOnboardingSessionStorageKey(scope));

/**
 * The person session and its company list are shared between tabs so that BankID is done once and
 * a second tab can open another company. Which company a tab is on is not shared, so it is kept
 * apart and merged back in on read.
 */
export const readPersonSession = (): OnboardingPersonSession | null => {
  const person = read<OnboardingPersonSession>(localStorage, ONBOARDING_PERSON_SESSION_KEY);
  if (!person) return null;
  return { ...person, activeCompanyId: read<string>(sessionStorage, ONBOARDING_ACTIVE_COMPANY_KEY) };
};

export const writePersonSession = (session: OnboardingPersonSession) => {
  localStorage.setItem(ONBOARDING_PERSON_SESSION_KEY, JSON.stringify({ ...session, activeCompanyId: null }));
  if (session.activeCompanyId) {
    sessionStorage.setItem(ONBOARDING_ACTIVE_COMPANY_KEY, JSON.stringify(session.activeCompanyId));
  } else {
    sessionStorage.removeItem(ONBOARDING_ACTIVE_COMPANY_KEY);
  }
};

export const removePersonSession = () => {
  localStorage.removeItem(ONBOARDING_PERSON_SESSION_KEY);
  sessionStorage.removeItem(ONBOARDING_ACTIVE_COMPANY_KEY);
  removeCompanySession(GENERIC_ONBOARDING_SCOPE);
};
